import {SidebarCollapseIcon, SidebarExpandIcon} from '@primer/octicons-react'
import {ActionList, PageLayout} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode, UIEvent as ReactUIEvent} from 'react'
import {createContext, use, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'

import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import {SidebarTooltip} from '../SidebarTooltip/SidebarTooltip'
import styles from './CollapsibleSidebar.module.css'
import {SidebarResizeController} from './SidebarResizeController'
import {getSeededSidebarWidth} from './use-resizable-sidebar-width'

// Keep in sync with --motion-duration-short, used by --motion-transition-stateChange in the sidebar CSS.
const TRANSITION_DURATION = 200

const scrollPositions = new Map<string, number>()

type CollapsibleSidebarContextValue = {
  isExpanded: boolean
  /** Whether the sidebar is fully expanded and its width transition has settled. */
  isExpansionSettled: boolean
  /**
   * Delayed mirror of `isExpanded` that stays in the old state during the
   * CSS width transition so descendants can keep their DOM stable while the
   * sidebar animates. On expand it flips immediately; on collapse it flips
   * after the transition duration (`TRANSITION_DURATION` ms).
   */
  visualIsExpanded: boolean
  /** Stable DOM id of the sidebar wrapper, used by Toggle for aria-controls. */
  sidebarId?: string
  /** Whether the sidebar content is currently rendered inside a narrow-viewport Dialog. */
  isInDialog: boolean
  /**
   * Toggle the sidebar between expanded and condensed. When provided by
   * the surrounding `<CollapsibleSidebar>`, descendants like
   * `<CollapsibleSidebar.Toggle>` can read this from context instead of
   * having it threaded through props.
   */
  onToggle?: () => void
}

/**
 * Stable identity for the "no provider" default. We compare against this
 * sentinel in `useIsInsideCollapsibleSidebar` to distinguish between "I'm
 * being read because no provider is mounted" (in which case the default
 * is returned) and "I'm being read inside a real provider" (in which case
 * the provider passed a fresh object).
 */
const DEFAULT_CONTEXT_VALUE: CollapsibleSidebarContextValue = {
  isExpanded: true,
  isExpansionSettled: true,
  visualIsExpanded: true,
  isInDialog: false,
}

export const CollapsibleSidebarContext = createContext<CollapsibleSidebarContextValue>(DEFAULT_CONTEXT_VALUE)

/**
 * Read the current expanded/collapsed state of the enclosing
 * `<CollapsibleSidebar>`. Defaults to `isExpanded: true` if no provider is
 * present, so individual primitives render correctly when used outside of a
 * collapsible layout (e.g. in a non-collapsible `PageLayout.Pane`).
 */
export function useCollapsibleSidebarContext(): CollapsibleSidebarContextValue {
  return use(CollapsibleSidebarContext)
}

/**
 * Returns true when the calling component is rendered inside a real
 * `<CollapsibleSidebar>` provider (or any consumer-supplied wrapper that
 * renders `CollapsibleSidebarContext`). Returns false when no provider is
 * mounted and the default fallback context is being used. Useful for
 * primitives that want to enforce contracts (e.g. requiring a
 * `LeadingVisual` on every item) only when actually used in a collapsible
 * layout.
 */
export function useIsInsideCollapsibleSidebar(): boolean {
  return use(CollapsibleSidebarContext) !== DEFAULT_CONTEXT_VALUE
}

export interface CollapsibleSidebarProps {
  children: ReactNode
  isExpanded: boolean
  /** Removes the sidebar and its descendants from interaction and the accessibility tree. */
  inert?: boolean
  /**
   * Optional aria-label. When provided, the wrapper exposes a `complementary`
   * landmark to assistive technologies. Omit when the children already provide
   * their own landmark to avoid nesting landmarks.
   */
  ariaLabel?: string
  className?: string
  /**
   * The width of the sidebar when expanded. Mirrors PageLayout.Pane width values.
   */
  expandedWidth?: 'small' | 'medium' | 'large'
  /**
   * When `true`, the expanded sidebar gains a drag handle on its trailing edge
   * so users can resize it (pointer drag, arrow keys, and double-click to reset
   * to `expandedWidth`). The condensed rail stays a fixed width. The chosen
   * width is persisted in a cookie via {@link SidebarResizeController}.
   */
  resizable?: boolean
  /**
   * Smallest width (px) the sidebar can be resized to when `resizable`.
   * @default 240
   */
  minWidth?: number
  /**
   * Largest width (px) the sidebar can be resized to when `resizable`.
   * @default 480
   */
  maxWidth?: number
  /**
   * Cookie key used to persist the resized width. Pass a custom key only when a
   * surface needs an independent width from the rest of the site.
   */
  widthStorageKey?: string
  /**
   * Server-read seed (px) so SSR markup matches the client's first paint and
   * avoids a width flash on hard navigation. Falls back to the `expandedWidth`
   * preset.
   */
  initialWidth?: number
  /** Called with the committed width whenever a resize operation ends. */
  onResizeEnd?: (width: number) => void
  /**
   * Content pinned to the bottom of the sidebar. Stays visible via
   * sticky positioning as the page scrolls, with an 8px bottom inset in fitViewport layouts. Typically
   * contains a `<CollapsibleSidebar.Toggle />` and/or a feedback link.
   */
  footer?: ReactNode
  /**
   * When `true`, the rail scrolls its own overflowing content vertically
   * (within its 100dvh height) instead of clipping it behind the sticky
   * footer. Defaults to `false` to preserve the original clip-and-fade
   * behavior for existing consumers. Opt in for sidebars whose content can
   * grow taller than the viewport (e.g. a long, paginated list).
   */
  scrollable?: boolean
  /** Preserves the scrollable rail's position when a sidebar with the same key remounts. */
  scrollRestorationKey?: string
  /**
   * Optional callback to toggle expanded ↔ condensed. When provided, it's
   * published via `CollapsibleSidebarContext` so descendants (e.g.
   * `<CollapsibleSidebar.Toggle>`) can read it from context instead of
   * threading it through props.
   */
  onToggle?: () => void
  'data-testid'?: string
}

const expandedWidthClass = {
  small: styles.expandedSmall,
  medium: styles.expandedMedium,
  large: styles.expandedLarge,
} as const

/**
 * A sidebar with a fixed icon-only condensed mode and an expanded mode.
 *
 * Switches between two discrete widths (condensed rail and expanded). Pass
 * `resizable` to add a drag handle on the expanded rail's trailing edge so
 * users can resize it (pointer drag, arrow keys, and double-click to reset);
 * the chosen width persists in a cookie. Pair it with `useCollapsibleSidebarState`
 * and render a `<CollapsibleSidebar.Toggle />` inside `children` to let users
 * change modes.
 *
 * Children rendered inside this component can call
 * `useCollapsibleSidebarContext()` (or use the `Item` primitive below) to
 * adapt their rendering to the current expanded/collapsed state without the
 * consumer having to thread the state through props.
 *
 * The wrapper is presentational by default — pass `ariaLabel` only when the
 * children do not already provide their own landmark.
 */
function CollapsibleSidebarRoot({
  children,
  isExpanded,
  inert,
  ariaLabel,
  className,
  expandedWidth = 'small',
  resizable = false,
  minWidth = 240,
  maxWidth = 480,
  widthStorageKey,
  initialWidth,
  onResizeEnd,
  footer,
  scrollable = false,
  scrollRestorationKey,
  onToggle,
  'data-testid': dataTestId,
}: CollapsibleSidebarProps) {
  const Wrapper = ariaLabel ? 'aside' : 'div'
  const sidebarId = useId()
  const sidebarRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLDivElement>(null)
  const parentContext = useCollapsibleSidebarContext()
  const prefersReducedMotion = usePrefersReducedMotion()

  // Resize only applies to the expanded rail; the condensed rail is a fixed
  // width and is never rendered inside the narrow-viewport Dialog. The width
  // behavior (window resize listener + width-cookie subscription in
  // `useResizableSidebarWidth`) lives in `<SidebarResizeController>`, mounted
  // only in this case, so non-resizable/condensed/in-dialog sidebars don't pay
  // for it.
  const showResizeHandle = resizable && isExpanded && !parentContext.isInDialog

  // Seeds the sidebar's `--sidebar-width` for SSR and the first client frame.
  // Once `<SidebarResizeController>` mounts it reports the live width up via
  // `setResizedWidth`, so this pure seed is only used until then.
  const [resizedWidth, setResizedWidth] = useState<number>()
  const sidebarWidth = resizedWidth ?? getSeededSidebarWidth({expandedWidth, minWidth, maxWidth, initialWidth})
  const sidebarMaxWidth = `min(${maxWidth}px, max(${minWidth}px, calc(100vw - var(--pane-max-width-diff, 511px))))`

  // Delayed mirror of `isExpanded`: on expand it flips immediately so the
  // new label text reveals as the width grows; on collapse it stays true
  // for the duration of the CSS transition so the DOM stays stable while
  // the sidebar shrinks, then flips to false once it has settled.
  const [visualIsExpanded, setVisualIsExpanded] = useState(isExpanded)

  // Expand: flip synchronously during render (set-state-during-render) so
  // SidebarTooltip is suppressed on the very same frame — no intermediate
  // paint where the tooltip is briefly enabled.
  if (isExpanded && !visualIsExpanded) {
    setVisualIsExpanded(true)
  }

  // Gates whether long labels may wrap (`white-space: normal`). Wrapping is only
  // allowed once the sidebar is settled at full expanded width — otherwise the
  // label would wrap/unwrap through the narrow intermediate widths during the
  // width animation, making the rows jump vertically. On collapse we drop it
  // synchronously (before the width shrinks) so no reflow happens mid-animation.
  const [allowWrap, setAllowWrap] = useState(isExpanded)

  if (!isExpanded) {
    if (allowWrap) setAllowWrap(false)
    if (visualIsExpanded && prefersReducedMotion) setVisualIsExpanded(false)
  } else if (!allowWrap && prefersReducedMotion) {
    // Reduced motion has no width transition to settle, so allow wrapping
    // immediately (set-state-during-render) rather than after a timer — the
    // sidebar snaps to full width, so there's no intermediate reflow to avoid.
    setAllowWrap(true)
  }

  // Changing `expandedWidth` while expanded swaps between width classes, which
  // animates the sidebar width just like expanding does. Drop wrapping for the
  // duration of that transition (re-enabled by the effect below) so long labels
  // don't reflow through the intermediate widths and jitter the row heights.
  // Reduced motion snaps instantly, so there's no transition to guard against.
  const [prevExpandedWidth, setPrevExpandedWidth] = useState(expandedWidth)
  if (expandedWidth !== prevExpandedWidth) {
    setPrevExpandedWidth(expandedWidth)
    if (isExpanded && allowWrap && !prefersReducedMotion) {
      setAllowWrap(false)
    }
  }

  useEffect(() => {
    // Reduced motion resolves collapse synchronously during render.
    if (prefersReducedMotion || isExpanded) return

    const id = setTimeout(() => setVisualIsExpanded(false), TRANSITION_DURATION)
    return () => clearTimeout(id)
  }, [isExpanded, prefersReducedMotion])

  useEffect(() => {
    // Reduced motion has no transition to wait on, and collapsed mode never
    // allows wrapping.
    if (prefersReducedMotion || !isExpanded) return

    // Re-arms whenever `expandedWidth` changes so the wrapping gate settles
    // after width-class transitions (not just the initial expand).
    const id = setTimeout(() => setAllowWrap(true), TRANSITION_DURATION)
    return () => clearTimeout(id)
  }, [isExpanded, prefersReducedMotion, expandedWidth])

  const contextValue = useMemo(
    () => ({
      isExpanded,
      isExpansionSettled: isExpanded && allowWrap,
      visualIsExpanded,
      sidebarId,
      isInDialog: parentContext.isInDialog,
      onToggle,
    }),
    [isExpanded, allowWrap, visualIsExpanded, sidebarId, parentContext.isInDialog, onToggle],
  )
  const bodyRef = useRef<HTMLDivElement>(null)
  const pendingScrollPositionRef = useRef<number | null>(null)
  const restorationScrollTopRef = useRef<number | null>(null)
  const scrollRestorationObserverRef = useRef<MutationObserver | null>(null)
  const clearPendingScrollRestoration = useCallback(() => {
    pendingScrollPositionRef.current = null
    restorationScrollTopRef.current = null
    scrollRestorationObserverRef.current?.disconnect()
    scrollRestorationObserverRef.current = null
  }, [])
  const setBodyRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return

      clearPendingScrollRestoration()
      bodyRef.current = node
      const clearBodyRef = () => {
        if (bodyRef.current === node) {
          bodyRef.current = null
        }
      }

      if (scrollRestorationKey) {
        pendingScrollPositionRef.current = scrollPositions.get(scrollRestorationKey) ?? 0
        const restorePendingScrollPosition = () => {
          const targetScrollTop = pendingScrollPositionRef.current
          if (targetScrollTop === null) return

          node.scrollTo({top: targetScrollTop})
          if (node.scrollTop >= targetScrollTop - 1) {
            clearPendingScrollRestoration()
          } else {
            restorationScrollTopRef.current = node.scrollTop
          }
        }

        const observer = new MutationObserver(restorePendingScrollPosition)
        scrollRestorationObserverRef.current = observer
        observer.observe(node, {attributes: true, characterData: true, childList: true, subtree: true})
        restorePendingScrollPosition()

        return () => {
          observer.disconnect()
          if (scrollRestorationObserverRef.current === observer) {
            clearPendingScrollRestoration()
          }
          clearBodyRef()
        }
      }

      return clearBodyRef
    },
    [clearPendingScrollRestoration, scrollRestorationKey],
  )
  const handleBodyScroll = useCallback(
    (event: ReactUIEvent<HTMLDivElement>) => {
      if (!scrollRestorationKey) return

      const scrollTop = event.currentTarget.scrollTop
      const restorationScrollTop = restorationScrollTopRef.current
      restorationScrollTopRef.current = null

      // Ignore only the clamped position produced by our latest restore attempt.
      if (
        pendingScrollPositionRef.current !== null &&
        restorationScrollTop !== null &&
        Math.abs(scrollTop - restorationScrollTop) < 1
      ) {
        return
      }

      clearPendingScrollRestoration()
      scrollPositions.set(scrollRestorationKey, scrollTop)
    },
    [clearPendingScrollRestoration, scrollRestorationKey],
  )
  const scrollEndRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [isClipping, setIsClipping] = useState(false)

  const hasFooter = footer != null
  useEffect(() => {
    const sentinel = scrollable ? scrollEndRef.current : sentinelRef.current
    const root = scrollable ? bodyRef.current : null
    if (!sentinel || !hasFooter) return

    const observer = new IntersectionObserver(entries => setIsClipping(!entries[0]?.isIntersecting), {
      root,
      threshold: 0,
    })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasFooter, scrollable])

  return (
    <CollapsibleSidebarContext value={contextValue}>
      <Wrapper
        ref={sidebarRef}
        id={sidebarId}
        inert={inert}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        data-allow-wrap={allowWrap || undefined}
        data-expanded={isExpanded}
        data-transitioning={(isExpanded ? !allowWrap : visualIsExpanded) || undefined}
        className={clsx(
          styles.sidebar,
          isExpanded ? (showResizeHandle ? styles.resizable : expandedWidthClass[expandedWidth]) : styles.condensed,
          className,
        )}
        style={
          showResizeHandle
            ? {[`--sidebar-width`]: `${sidebarWidth}px`, [`--pane-max-width`]: sidebarMaxWidth}
            : undefined
        }
      >
        {scrollable ? (
          <div
            ref={setBodyRef}
            className={styles.bodyScrollable}
            onScroll={scrollRestorationKey ? handleBodyScroll : undefined}
          >
            {children}
            {footer != null ? <div ref={scrollEndRef} className={styles.sentinel} /> : null}
          </div>
        ) : (
          children
        )}
        {footer != null ? (
          <>
            {!scrollable ? <div ref={sentinelRef} className={styles.sentinel} /> : null}
            <div className={clsx(styles.footer, isClipping && styles.footerClipping)}>{footer}</div>
          </>
        ) : null}
        {showResizeHandle ? (
          <SidebarResizeController
            sidebarRef={sidebarRef}
            handleRef={handleRef}
            expandedWidth={expandedWidth}
            minWidth={minWidth}
            maxWidth={maxWidth}
            widthStorageKey={widthStorageKey}
            initialWidth={initialWidth}
            onResizeEnd={onResizeEnd}
            onWidthChange={setResizedWidth}
          />
        ) : null}
      </Wrapper>
    </CollapsibleSidebarContext>
  )
}

export interface CollapsibleSidebarToggleProps {
  /**
   * Optional override for the surrounding `<CollapsibleSidebar>`'s
   * `isExpanded` value. When omitted, the Toggle reads from
   * `CollapsibleSidebarContext` so callers don't need to thread it
   * through props.
   */
  isExpanded?: boolean
  /**
   * Optional override for the surrounding `<CollapsibleSidebar>`'s
   * `onToggle` handler. When omitted, the Toggle reads from
   * `CollapsibleSidebarContext`. If neither is set, the Toggle is a no-op.
   */
  onToggle?: () => void
  /**
   * Override the default labels (Collapse sidebar / Expand sidebar).
   */
  collapseLabel?: string
  expandLabel?: string
  className?: string
}

export type CollapsibleSidebarToggleItemProps = CollapsibleSidebarToggleProps

/**
 * A toggle button intended to live at the bottom of a `<CollapsibleSidebar>`.
 * Uses `ActionList.Item` so the sizing, color, hover/focus states match the
 * NavList items in the sidebar. In condensed mode, the text label is
 * visually hidden by the `.sidebarCondensed` CSS overrides on the consumer.
 *
 * `isExpanded` and `onToggle` are read from `CollapsibleSidebarContext`
 * by default; pass them explicitly only when you need to override the
 * surrounding sidebar's state (e.g. for a story or test).
 */
function SidebarToggle({
  isExpanded: isExpandedProp,
  onToggle: onToggleProp,
  collapseLabel = 'Collapse sidebar',
  expandLabel = 'Expand sidebar',
  className,
  standalone,
}: CollapsibleSidebarToggleProps & {standalone: boolean}) {
  const ctx = use(CollapsibleSidebarContext)
  const isExpanded = isExpandedProp ?? ctx.isExpanded
  const onToggle = onToggleProp ?? ctx.onToggle
  const sidebarId = ctx.sidebarId
  const visualIsExpanded = isExpandedProp != null ? isExpanded : ctx.visualIsExpanded

  // The Toggle exists to collapse/expand the sidebar rail. When the sidebar
  // contents are rendered inside a Dialog (mobile breakpoint), there's no
  // rail to collapse — the dialog is open or closed — so suppress the button
  // entirely to avoid a misleading "Collapse sidebar" affordance.
  if (ctx.isInDialog) return null

  const label = isExpanded ? collapseLabel : expandLabel
  const visualLabel = visualIsExpanded ? collapseLabel : expandLabel
  const Icon = visualIsExpanded ? SidebarExpandIcon : SidebarCollapseIcon

  const item = (
    <ActionList.Item
      className={standalone ? undefined : className}
      onSelect={onToggle}
      aria-expanded={isExpanded}
      aria-controls={sidebarId}
    >
      <ActionList.LeadingVisual>
        <Icon />
      </ActionList.LeadingVisual>
      {visualLabel}
    </ActionList.Item>
  )
  const content = standalone ? <ActionList className={clsx(styles.toggleList, className)}>{item}</ActionList> : item

  return (
    <SidebarTooltip text={label} disabled={isExpanded}>
      {content}
    </SidebarTooltip>
  )
}

function CollapsibleSidebarToggle(props: CollapsibleSidebarToggleProps) {
  return <SidebarToggle {...props} standalone />
}

function CollapsibleSidebarToggleItem(props: CollapsibleSidebarToggleItemProps) {
  return <SidebarToggle {...props} standalone={false} />
}

// Item primitive removed — consumers render their own items and read
// `useCollapsibleSidebarContext()` to branch on expanded/condensed state.

export const CollapsibleSidebar = Object.assign(CollapsibleSidebarRoot, {
  __SLOT__: PageLayout.Sidebar.__SLOT__, // Reuse the same slot as PageLayout.Sidebar for easy composition
  Toggle: CollapsibleSidebarToggle,
  ToggleItem: CollapsibleSidebarToggleItem,
})

// Re-export sub-components as named exports so react-docgen-typescript can
// extract their prop types when referenced via `<ArgTypes of={...} />` in
// MDX docs. The compound-component pattern (Object.assign) hides these from
// the docgen plugin, which only walks top-level module exports.
export {CollapsibleSidebarRoot}
export {CollapsibleSidebarToggle}
export {CollapsibleSidebarToggleItem}
